import React from 'react';

import styled, { css } from 'styled-components';
import { SECONDARY_HOVER_GRAY } from '../../../theme';
import Text, { TextColor, TextKind, textStyleFrom } from '../Text';
import { SizeProp } from '../util';

const StyledTag = styled.div<{
  $rounded?: boolean;
  $background?: string;
  $color?: string;
  $size: SizeProp;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $size }) =>
    ({
      [SizeProp.Small]: css`
        gap: 0.25rem;
        padding: 2px 4px;
      `,
      [SizeProp.Medium]: css`
        gap: 0.5rem;
        padding: 4px 8px;
      `,
    })[$size]}

  ${({ $rounded }) =>
    $rounded
      ? css`
          border-radius: 16px;
        `
      : css`
          border-radius: 4px;
        `};

  background-color: ${({ $background }) => $background || SECONDARY_HOVER_GRAY};

  ${textStyleFrom(TextKind.ButtonM, TextColor.Primary)};
  color: ${({ $color }) =>
    $color || TextColor.Primary}; // overrides textStyleFrom color
`;

interface TagProps extends React.PropsWithChildren {
  rounded?: boolean;
  background?: string;
  color?: string;
  testId?: string;
  size?: SizeProp;
}

export const Tag: React.FC<TagProps> = ({
  children,
  rounded,
  background,
  color,
  testId,
  size = SizeProp.Medium,
}) => (
  <StyledTag
    $rounded={rounded}
    $background={background}
    $color={color}
    $size={size}
    data-testid={testId}
  >
    {children}
  </StyledTag>
);

const TagGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export type TagGroupProps = {
  className?: string;
  data?: string[] | null;
  label?: string | null;
  labelTestId?: string;
  answerTestId?: string;
  rounded?: boolean;
};

const TagGroup: React.FC<TagGroupProps> = ({
  label = '',
  data = [],
  labelTestId,
  answerTestId,
  rounded,
}) => (
  <TagGroupContainer data-testid={`${label} Group`}>
    {label && (
      <Text color={TextColor.Secondary} testId={labelTestId}>
        {label}
      </Text>
    )}
    <TagContainer>
      {data?.map((answer) => (
        <Tag rounded={rounded} key={answer}>
          <Text testId={answerTestId}>{answer}</Text>
        </Tag>
      ))}
    </TagContainer>
  </TagGroupContainer>
);

export default TagGroup;
